import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { transferRecordConstants as trConstants } from 'locale/transferRecords'
import {
  EmptyState,
  Spinner,
  ExpandableCard,
  ExpandableCardField
} from '@lmc/ui-kit'
import { TransferRecordResponse } from 'types/transferRecord'
import { useInfiniteScrollFetch } from '../../../../../hooks/useInfiniteScroll'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

const formatDate = (date: string | undefined): string => {
  if (!date) return ''
  return dayjs(date).format('DD/MM/YYYY')
}

const formatDateTime = (dateTime: string | undefined): string => {
  if (!dateTime) return ''
  return dayjs.utc(dateTime).local().format('DD/MM/YYYY HH:mm')
}

interface TransferRecordListProps {
  records: TransferRecordResponse[]
  isLoading: boolean
  fetchNextPage: () => void
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

const getRecordTypeLabel = (
  type: string,
  t: (key: string) => string
): string => {
  switch (type) {
    case 'hospitalisation':
      return t(trConstants.HOSPITALISATION)
    case 'transfer':
      return t(trConstants.TRANSFER)
    case 'return':
      return t(trConstants.RETURN)
    default:
      return type
  }
}

export const TransferRecordList: React.FC<TransferRecordListProps> = ({
  records,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage
}) => {
  const { t } = useTranslation(NAMESPACE.TRANSFER_RECORDS)
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null)

  const { infiniteRef, rootRef } = useInfiniteScrollFetch({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  })

  if (isLoading) {
    return <SpinnerContainer><Spinner /></SpinnerContainer>
  }

  if (records.length === 0) {
    return (
      <EmptyState
        title={t(trConstants.NO_TRANSFER_RECORDS)}
        description={t(trConstants.NO_TRANSFER_RECORDS_DESCRIPTION)}
      />
    )
  }

  const toggleExpand = (recordId: string) => {
    setExpandedRecordId(expandedRecordId === recordId ? null : recordId)
  }

  const getDateLabel = (type: string): string => {
    if (type === 'hospitalisation') return t(trConstants.DATE_OF_HOSPITALISATION)
    if (type === 'return') return t(trConstants.DATE_OF_RETURN)
    return t(trConstants.DATE_OF_TRANSFER)
  }

  const buildFields = (record: TransferRecordResponse): ExpandableCardField[] => {
    const fields: ExpandableCardField[] = []

    fields.push({
      label: t(trConstants.RECORD_TYPE),
      value: getRecordTypeLabel(record.type, t),
      fullWidth: false
    })

    if (record.date) {
      fields.push({
        label: getDateLabel(record.type),
        value: formatDate(record.date),
        fullWidth: false
      })
    }

    if (record.transferredFrom?.locationName) {
      const fromParts = [record.transferredFrom.locationName]
      if (record.transferredFrom.address) fromParts.push(record.transferredFrom.address)
      if (record.transferredFrom.careSettingType) fromParts.push(record.transferredFrom.careSettingType)
      fields.push({
        label: t(trConstants.TRANSFERRED_FROM_LABEL),
        value: fromParts.join(', '),
        fullWidth: true
      })
    }

    if (record.transferredTo?.locationName) {
      const toParts = [record.transferredTo.locationName]
      if (record.transferredTo.address) toParts.push(record.transferredTo.address)
      if (record.transferredTo.careSettingType) toParts.push(record.transferredTo.careSettingType)
      fields.push({
        label: t(trConstants.TRANSFERRED_TO_LABEL),
        value: toParts.join(', '),
        fullWidth: true
      })
    }

    if (record.createdBy?.name) {
      const createdByName = `${record.createdBy.name.first} ${record.createdBy.name.last}`
      fields.push({
        label: t(trConstants.CREATED_BY),
        value: record.createdAt
          ? t(trConstants.CREATED_BY_ON_DATE, { name: createdByName, date: formatDateTime(record.createdAt) })
          : createdByName,
        fullWidth: false
      })
    }

    return fields
  }

  const renderRecord = (record: TransferRecordResponse) => {
    const dateText = formatDate(record.date)
    const fields = buildFields(record)
    const addedBy = record.createdBy?.name
      ? t(trConstants.ADDED_BY, { name: `${record.createdBy.name.first} ${record.createdBy.name.last}` })
      : undefined

    return (
      <ExpandableCard
        key={record._id}
        id={record._id}
        title={record.reason || ''}
        date={
          dateText
            ? `${dateText}${addedBy ? ` \u00B7 ${addedBy}` : ''}`
            : undefined
        }
        fields={fields}
        isExpanded={expandedRecordId === record._id}
        onToggleExpand={fields.length > 0 ? toggleExpand : undefined}
        testId={`transfer-record-${record._id}`}
      />
    )
  }

  return (
    <ScrollContainer ref={rootRef}>
      <ListContainer>
        {records.map(renderRecord)}

        {(hasNextPage || isFetchingNextPage) && (
          <LoadMoreContainer ref={infiniteRef}>
            {isFetchingNextPage && <Spinner />}
          </LoadMoreContainer>
        )}
      </ListContainer>
    </ScrollContainer>
  )
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px auto 0px auto;
  padding-bottom: 40px;
  max-width: 1080px;
`

const ScrollContainer = styled.div`
  overflow-y: auto;
  height: 100%;
`

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`
